/** @typedef {import("../../../api/types").Order} Order */
import {useState} from "react";
import axios from "axios";
import {useNavigate} from "react-router-dom";

const ORDERS_URL = "/os/api/service_orders/";

/**
 * Runs an order's status changes (and cancel-and-clone), one at a time:
 * `ask` picks one, which the page confirms in a dialog (`run`) or drops
 * (`dismiss`). Shared by the buttons and the menu that start them.
 *
 * @param {Order} order
 * @param {(order: Order) => void} onUpdated
 */
export const useOrderAction = (order, onUpdated) => {
    const [pending, setPending] = useState(/** @type {import("./actionConfig").ActionName|null} */ (null));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(/** @type {string|null} */ (null));
    const navigate = useNavigate();
    const url = `${ORDERS_URL}${order.id}/`;

    const run = async () => {
        if (!pending) return;
        setBusy(true);
        setError(null);
        try {
            const clone = pending === "cancelAndClone";
            const {data} = await axios.post(`${url}${clone ? "cancel" : pending}/`);
            if (clone) {
                navigate("/orders/new", {state: {cloneFrom: order}});
            } else {
                onUpdated(data);
            }
        } catch (err) {
            if (err.response?.status === 409) {
                setError("O status da ordem mudou e esta ação não está mais disponível.");
                // Show what the order looks like now.
                try {
                    onUpdated((await axios.get(url)).data);
                } catch {
                    // Keep the stale view; the message above still applies.
                }
            } else {
                setError("Não foi possível concluir a ação. Tente novamente.");
            }
        } finally {
            setBusy(false);
            setPending(null);
        }
    };

    return {pending, busy, error, ask: setPending, dismiss: () => setPending(null), run};
};
